import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Viewport-based page size, ported from files-viewer's
// hooks/useViewportPageSize.ts (same pattern as this dashboard's own
// Home patient table / Messages tables): fit as many rows as the visible
// table area allows so the page never scrolls. Attach the refs to the table
// card, thead, tbody, and pagination wrapper, and call remeasureOnce once
// real rows are rendered (the loading row has a different height than data
// rows, which would otherwise skew the very first measurement).
//
// One deviation from files-viewer's version: that hook floors at
// `Math.max(1, ...)` — no built-in minimum beyond "at least one row". This
// port adds MIN_PAGE_SIZE (5), matching the floor this dashboard's own
// PatientTable.tsx / Messages.tsx already use for the identical viewport-fit
// pattern, so a very short window never collapses the table to 1-2 rows.

const CARD_BOTTOM_SPACE = 34; // the card wrapper's pb-8 padding (32px) + borders
const FALLBACK_ROW_HEIGHT = 56; // h-14
const MIN_PAGE_SIZE = 5;

interface ViewportPageSize {
    pageSize: number;
    tableCardRef: React.RefObject<HTMLDivElement>;
    tableHeaderRef: React.RefObject<HTMLTableSectionElement>;
    tableBodyRef: React.RefObject<HTMLTableSectionElement>;
    paginationRef: React.RefObject<HTMLDivElement>;
    remeasure: () => void;
    // One-shot remeasure for the "first real rows rendered" layout effects.
    remeasureOnce: () => void;
}

export function useViewportPageSize(defaultPageSize = 20): ViewportPageSize {
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const tableCardRef = useRef<HTMLDivElement>(null);
    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const paginationRef = useRef<HTMLDivElement>(null);

    const remeasure = useCallback(() => {
        if (!tableCardRef.current || !tableHeaderRef.current || !tableBodyRef.current) return;
        const firstRow = tableBodyRef.current.querySelector('tr');
        if (!firstRow) return;
        const cardTop = tableCardRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const headerHeight = tableHeaderRef.current.offsetHeight;
        const paginationHeight = paginationRef.current?.offsetHeight || 0;
        const rowHeight = firstRow.offsetHeight || FALLBACK_ROW_HEIGHT;
        const availableHeight = Math.max(
            0,
            viewportHeight - cardTop - headerHeight - paginationHeight - CARD_BOTTOM_SPACE
        );
        setPageSize(Math.max(MIN_PAGE_SIZE, Math.floor(availableHeight / rowHeight)));
    }, []);

    // Pages remeasure when real data rows first land (the loading row's height
    // skews the initial measurement) - but only ONCE. Remeasuring on every
    // row-count change feeds pageSize -> refetch -> row count -> remeasure back
    // into itself, which can oscillate between two page sizes forever
    // (React error #185, maximum update depth).
    const measuredRealRows = useRef(false);
    const remeasureOnce = useCallback(() => {
        if (measuredRealRows.current) return;
        measuredRealRows.current = true;
        remeasure();
    }, [remeasure]);

    useLayoutEffect(() => {
        remeasure();
        window.addEventListener('resize', remeasure);
        return () => window.removeEventListener('resize', remeasure);
    }, [remeasure]);

    return {
        pageSize,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef,
        remeasure,
        remeasureOnce
    };
}
